import { Mutation, JsonApiRequestError } from '@farfetched/core';
import { chain } from '@leprecoins/effector';
import { sample, Store } from 'effector';
import {
	Cashdesk,
	createDepositCashdeskMutation,
	DepositCashdeskMutationParams,
	UuidResponse,
} from 'shared/api';
import { createModalFormModel, greaterZeroRule, ModalFormModel, notify } from 'shared/lib';

export type DepositCashdeskFormValue = {
	amount: string;
};

export type DepositCashdeskModel = ModalFormModel<Cashdesk, DepositCashdeskFormValue> & {
	depositCashdeskMutation: Mutation<
		DepositCashdeskMutationParams,
		UuidResponse,
		JsonApiRequestError
	>;
	$pending: Store<boolean>;
};

export const createDepositCashdeskModel = (): DepositCashdeskModel => {
	const depositCashdeskMutation = createDepositCashdeskMutation();

	const model = createModalFormModel<Cashdesk, DepositCashdeskFormValue>({
		fields: {
			amount: {
				init: '',
				rules: [greaterZeroRule],
			},
		},
		resetErrorsOnChange: true,
		validateOn: ['submit'],
	});

	const { $pending } = depositCashdeskMutation;

	sample({
		clock: model.form.submitted,
		source: {
			amount: model.form.fields.amount.$value,
			cashdesk: model.$modalData,
		},
		filter: ({ cashdesk }) => cashdesk !== null,
		fn: ({ amount, cashdesk }) => ({
			amount: Number(amount),
			cashdeskId: cashdesk!.id,
		}),
		target: depositCashdeskMutation.start,
	});

	chain(depositCashdeskMutation.finished.success, model.switcher.disable);

	notify(depositCashdeskMutation.finished.success, 'deposit-cashdesk.success');

	chain(model.modalClosed, depositCashdeskMutation.reset);

	return {
		depositCashdeskMutation,
		$pending,
		...model,
	};
};
